import apiClient from './client'
import type {
  GroupPaymentConfig,
  AthleteBalance,
  PaymentTransaction,
  AthletePaymentOverride,
  CreateGroupPaymentConfigRequest,
  UpdateGroupPaymentConfigRequest,
  RecordPaymentRequest,
  CreateOverrideRequest,
  UpdateOverrideRequest,
} from '@/types/payment_simple'
import { rublesToKopecks } from '@/types/payment_simple'

export const paymentSimpleApi = {
  // === Group Payment Config ===

  // POST /api/v1/groups/{groupId}/payment-config
  createGroupConfig: async (groupId: string, data: CreateGroupPaymentConfigRequest): Promise<GroupPaymentConfig> => {
    const response = await apiClient.post<GroupPaymentConfig>(`/groups/${groupId}/payment-config`, {
      base_price: rublesToKopecks(data.base_price),
      billing_day: data.billing_day,
      currency: data.currency || 'RUB',
      description: data.description || '',
    })
    return response.data
  },

  // GET /api/v1/groups/{groupId}/payment-config
  getGroupConfig: async (groupId: string): Promise<GroupPaymentConfig> => {
    const response = await apiClient.get<GroupPaymentConfig>(`/groups/${groupId}/payment-config`)
    return response.data
  },

  // PUT /api/v1/groups/{groupId}/payment-config
  updateGroupConfig: async (groupId: string, data: UpdateGroupPaymentConfigRequest): Promise<GroupPaymentConfig> => {
    const response = await apiClient.put<GroupPaymentConfig>(`/groups/${groupId}/payment-config`, {
      base_price: rublesToKopecks(data.base_price),
      billing_day: data.billing_day,
      description: data.description || '',
    })
    return response.data
  },

  // DELETE /api/v1/groups/{groupId}/payment-config
  deleteGroupConfig: async (groupId: string): Promise<void> => {
    await apiClient.delete(`/groups/${groupId}/payment-config`)
  },

  // === Athlete Balance ===

  // GET /api/v1/athletes/{athleteId}/payment/balance
  getAthleteBalance: async (athleteId: string): Promise<AthleteBalance> => {
    const response = await apiClient.get<AthleteBalance>(`/athletes/${athleteId}/payment/balance`)
    return response.data
  },

  // GET /api/v1/groups/{groupId}/balances
  getGroupBalances: async (groupId: string): Promise<AthleteBalance[]> => {
    const response = await apiClient.get<AthleteBalance[]>(`/groups/${groupId}/balances`)
    return response.data
  },

  // === Payment Transactions ===

  // POST /api/v1/athletes/{athleteId}/payment
  recordPayment: async (athleteId: string, data: RecordPaymentRequest): Promise<PaymentTransaction> => {
    const response = await apiClient.post<PaymentTransaction>(`/athletes/${athleteId}/payment`, {
      group_id: data.group_id,
      amount: rublesToKopecks(data.amount),
      payment_method: data.payment_method,
      description: data.description || '',
      transaction_date: data.transaction_date,
    })
    return response.data
  },

  // GET /api/v1/athletes/{athleteId}/payment/transactions?limit=50
  getAthleteTransactions: async (athleteId: string, limit = 50): Promise<PaymentTransaction[]> => {
    const response = await apiClient.get<PaymentTransaction[]>(`/athletes/${athleteId}/payment/transactions`, {
      params: { limit },
    })
    return response.data
  },

  // DELETE /api/v1/payment-simple/transactions/{transactionId}
  deleteTransaction: async (transactionId: string): Promise<void> => {
    await apiClient.delete(`/payment-simple/transactions/${transactionId}`)
  },

  // === Payment Overrides (индивидуальные цены) ===

  // POST /api/v1/athletes/{athleteId}/payment/override
  createOverride: async (athleteId: string, data: CreateOverrideRequest): Promise<AthletePaymentOverride> => {
    const response = await apiClient.post<AthletePaymentOverride>(`/athletes/${athleteId}/payment/override`, {
      group_id: data.group_id,
      custom_price: rublesToKopecks(data.custom_price),
      reason: data.reason,
      currency: data.currency || 'RUB',
    })
    return response.data
  },

  // GET /api/v1/athletes/{athleteId}/payment/override
  getOverride: async (athleteId: string): Promise<AthletePaymentOverride> => {
    const response = await apiClient.get<AthletePaymentOverride>(`/athletes/${athleteId}/payment/override`)
    return response.data
  },

  // PUT /api/v1/athletes/{athleteId}/payment/override
  updateOverride: async (athleteId: string, data: UpdateOverrideRequest): Promise<AthletePaymentOverride> => {
    const response = await apiClient.put<AthletePaymentOverride>(`/athletes/${athleteId}/payment/override`, {
      custom_price: rublesToKopecks(data.custom_price),
      reason: data.reason,
    })
    return response.data
  },

  // DELETE /api/v1/athletes/{athleteId}/payment/override
  deleteOverride: async (athleteId: string): Promise<void> => {
    await apiClient.delete(`/athletes/${athleteId}/payment/override`)
  },

  // GET /api/v1/groups/{groupId}/payment-overrides
  getGroupOverrides: async (groupId: string): Promise<AthletePaymentOverride[]> => {
    const response = await apiClient.get<AthletePaymentOverride[]>(`/groups/${groupId}/payment-overrides`)
    return response.data
  },
}
